'use client';

import { useState, useRef, useEffect } from 'react';

import Link from 'next/link';

import {
  BookOpen,
  ChevronDown,
  Moon,
  Globe,
  Sun,
  CreditCard,
  GitCompareArrows,
  Newspaper,
  LayoutGrid,
  Store,
} from 'lucide-react';

import AutoHeightImage from '@/components/cards/AutoHeightImage';
import { useTheme } from '@/context/ThemeContext';
import { CATEGORY_NAMES, CHANNEL_NAMES, CHANNEL_KEYS } from '@/data/credit-cards/constants/keys';

export default function Navbar() {
  const { theme, toggleTheme } = useTheme();
  const [creditCardDropdownOpen, setCreditCardDropdownOpen] = useState(false);
  const creditCardDropdownRef = useRef<HTMLDivElement>(null);

  // Function to close the dropdown menu
  const closeDropdown = () => {
    setCreditCardDropdownOpen(false);
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        creditCardDropdownRef.current &&
        !creditCardDropdownRef.current.contains(event.target as Node)
      ) {
        setCreditCardDropdownOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const navItemClass =
    'px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-indigo-600 dark:hover:text-indigo-400';
  const megaMenuItemClass =
    'flex items-center p-3 rounded-lg transition-all duration-200 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 hover:text-indigo-600 dark:hover:text-indigo-400 group';

  return (
    <div className="sticky top-0 z-50 w-full bg-white/80 dark:bg-gray-900/80 backdrop-blur-md border-b border-gray-100 dark:border-gray-800 transition-all duration-300 hover:shadow-sm">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 sm:h-20 justify-between items-center">
          {/* Logo */}
          <div className="flex items-center">
            <Link
              href="/"
              className="block transform transition-transform duration-200 hover:scale-105"
            >
              <AutoHeightImage
                src={'/logo.png'}
                alt="Logo"
                width={100}
                priority={false}
                className="h-10 w-auto"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="flex items-center space-x-1">
            {/* Credit Cards Mega Menu */}
            <div className="relative" ref={creditCardDropdownRef}>
              <button
                onClick={() => setCreditCardDropdownOpen(!creditCardDropdownOpen)}
                className={`${navItemClass} flex items-center space-x-1 ${
                  creditCardDropdownOpen
                    ? 'bg-gray-100 dark:bg-gray-800 text-indigo-600 dark:text-indigo-400'
                    : ''
                }`}
              >
                <span>信用卡</span>
                <ChevronDown
                  className={`h-4 w-4 transition-transform duration-200 ${creditCardDropdownOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {creditCardDropdownOpen && (
                <div className="absolute left-1/2 transform -translate-x-1/2 mt-2 w-[50vw] sm:w-[28rem] md:w-[36rem] lg:w-[40rem] bg-white dark:bg-gray-800 rounded-xl shadow-xl z-50 border border-gray-100 dark:border-gray-700 backdrop-blur-xl bg-opacity-95 dark:bg-opacity-95 p-4 sm:p-6 grid md:grid-cols-2 grid-cols-1 gap-4 md:gap-6 animate-fadeIn">
                  {/* Left Column */}
                  <div className="space-y-2">
                    <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 pl-1">
                      主要功能
                    </h3>
                    <Link
                      href="/credit-cards"
                      className={megaMenuItemClass}
                      onClick={closeDropdown}
                    >
                      <CreditCard className="h-5 w-5 mr-3 text-indigo-600 dark:text-indigo-400 group-hover:scale-110 transition-transform" />
                      <div>
                        <div className="font-medium">信用卡總覽</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          瀏覽所有信用卡
                        </div>
                      </div>
                    </Link>
                    <Link
                      href="/credit-cards/category"
                      className={megaMenuItemClass}
                      onClick={closeDropdown}
                    >
                      <BookOpen className="h-5 w-5 mr-3 text-indigo-600 dark:text-indigo-400 group-hover:scale-110 transition-transform" />
                      <div>
                        <div className="font-medium">信用卡分類</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          按照分類瀏覽
                        </div>
                      </div>
                    </Link>
                    <Link
                      href="/credit-cards/channel"
                      className={megaMenuItemClass}
                      onClick={closeDropdown}
                    >
                      <Globe className="h-5 w-5 mr-3 text-indigo-600 dark:text-indigo-400 group-hover:scale-110 transition-transform" />
                      <div>
                        <div className="font-medium">信用卡通路</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          按照通路瀏覽
                        </div>
                      </div>
                    </Link>
                    <Link
                      href="/credit-cards/compare"
                      className={megaMenuItemClass}
                      onClick={closeDropdown}
                    >
                      <GitCompareArrows className="h-5 w-5 mr-3 text-indigo-600 dark:text-indigo-400 group-hover:scale-110 transition-transform" />
                      <div>
                        <div className="font-medium">信用卡比較</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          比較不同卡片優惠
                        </div>
                      </div>
                    </Link>
                    <Link
                      href="/credit-cards/blog"
                      className={megaMenuItemClass}
                      onClick={closeDropdown}
                    >
                      <Newspaper className="h-5 w-5 mr-3 text-indigo-600 dark:text-indigo-400 group-hover:scale-110 transition-transform" />
                      <div>
                        <div className="font-medium">信用卡文章</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          最新優惠與攻略
                        </div>
                      </div>
                    </Link>
                  </div>

                  {/* Right Column */}
                  <div className="hidden md:grid grid-cols-2 gap-6">
                    <div>
                      <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 pl-1 flex items-center">
                        <LayoutGrid className="h-4 w-4 mr-2" />
                        熱門分類
                      </h3>
                      <div className="space-y-1">
                        {Object.entries(CATEGORY_NAMES).map(([key, name]) => (
                          <Link
                            key={key}
                            href={`/credit-cards/category/${key.replace(/\[\]|\[\]/g, '')}`}
                            className="block px-3 py-2 text-sm rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors duration-150"
                            onClick={closeDropdown}
                          >
                            {name}
                          </Link>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 pl-1 flex items-center">
                        <Store className="h-4 w-4 mr-2" />
                        熱門通路
                      </h3>
                      <div className="space-y-1">
                        {[
                          CHANNEL_KEYS.ONLINE_SHOPPING,
                          CHANNEL_KEYS.TRAVEL,
                          CHANNEL_KEYS.DINING,
                        ].map(key => (
                          <Link
                            key={key}
                            href={`/credit-cards/channel/${key}`}
                            className="block px-3 py-2 text-sm rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors duration-150"
                            onClick={closeDropdown}
                          >
                            {CHANNEL_NAMES[key]}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Other Navigation Items */}
            <Link href="/digital-banks" className={navItemClass}>
              數位帳戶
            </Link>
          </div>

          {/* Right Side Controls */}
          <div className="flex items-center space-x-2">
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
              aria-label={theme === 'dark' ? '切換至亮色模式' : '切換至暗色模式'}
            >
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
